import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import ProductApi from '../../../lib/api/product/ProductApi';
import { addAllItemToSelectList, setRequestForm } from '../../../lib/common';
import { setMinimumPrice2000WonCategoryIdList, setProductPayMethodTypeInfo } from '../../../lib/utils/productUtils';
import { PRODUCT } from '../../../constants/queryKeys';
import { PRODUCT_PAY_METHOD_VALUE_TYPE, PRODUCT_STATUS } from '../../../constants/service/app/productConstants';
import { COMMON_RES_CODE } from '../../../constants/responseCode/commonResCode';

const queryFn = async (payload) => {
  const result = await ProductApi.prepareInsertProduct(setRequestForm(undefined, true));

  if (result?.status !== COMMON_RES_CODE.SUCCESS) {
    throw result.msg;
  }

  return produce(payload, (draft) => {
    /**
     *  상품 1차 카테고리 리스트
     */
    draft.mainProductCategorySelectList = result.data.mainProductCategorySelectList;

    /**
     *  상품 2차 카테고리 리스트
     */
    draft.subProductCategorySelectList = result.data.subProductCategorySelectList;

    /**
     *  판매가가 2,000원 이상만 올릴 수 있는 1차 카테고리 id 리스트
     */
    draft.minimumPrice2000WonCategoryIdList = setMinimumPrice2000WonCategoryIdList(
      result.data.mainProductCategorySelectList.normal,
    );

    /**
     *  상품 등록 시에는 상품 상태를 승인 요청으로 제한함, 상태를 변경하고자 할 경우에는
     *  상품 승인 또는 거절 과정을 거치도록 함
     */
    draft.productDetail.proStatusAndBrandInfo.status = PRODUCT_STATUS.CONFIRM_READY;

    /**
     * 상품 이미지 및 상세 설명 초기화
     */
    draft.productDetail.proImg = {
      mainImg: [],
      subImg: [],
      notice: null,
    };
    draft.productDetail.proDescriptionImg = {
      detailImg: [],
      orderExampleMessage: null,
    };

    /**
     *  상점 리스트
     */
    draft.storeSelectList = addAllItemToSelectList(result.data.storeList);

    /**
     *  상품 속성
     */
    draft.productPropertyList = result.data.productPropertyList;

    /**
     *  상품 태그 그룹별 태그
     */
    draft.productTagInfo = result.data.productTagInfo;

    /**
     *  상품 결제 타입에 해당되는 결제수단 정보
     */
    draft.productPayMethodTypeInfo = setProductPayMethodTypeInfo(result.data.productPayMethodTypeAllList);

    /**
     *  상품 정보의 결제 가능 수단, 카드 옵션 설정
     */
    draft.productDetail.proInfo.productPayMethodList =
      draft.productPayMethodTypeInfo[draft.productDetail.proInfo.productPayMethodTypeId].list;
    draft.productDetail.proInfo.card =
      draft.productPayMethodTypeInfo[draft.productDetail.proInfo.productPayMethodTypeId].card ||
      PRODUCT_PAY_METHOD_VALUE_TYPE.UN_USED;
  });
};

const usePrepareInsertProduct = (options) => {
  const query = useQuery([...PRODUCT.PRODUCT_DETAIL], () => queryFn(options.initialData), {
    ...options,
    suspense: false,
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    return () => {
      queryClient.removeQueries([...PRODUCT.PRODUCT_DETAIL]);
    };
  }, []);

  return query;
};

export default usePrepareInsertProduct;
